import { gql } from '@apollo/client';

// --- Per-entity search queries (Atlas Search autocomplete + fuzzy) ---

export const SEARCH_SCHEDULES = gql`
  query SearchSchedules($teamId: String, $input: SearchInput!) {
    searchSchedules(teamId: $teamId, input: $input) {
      _id
      name
      teamId
      path
      tags
      defaultAssetId
      createdAt
      lastUpdatedDate
      parentGroupInfo {
        _id
        name
        path
        teamId
        isFolderSecurityEdit
        isFolderSecurityView
        securities {
          userId
          role
        }
        securityTeams
      }
    }
  }
`;

export const SEARCH_DEVICES = gql`
  query SearchDevices($teamId: String, $input: SearchInput!) {
    searchDevices(teamId: $teamId, input: $input) {
      _id
      UUID
      deviceName
      name
      accountId
      teamId
      path
      lastHeartBeat
      status
      os
      device
      model
      platform
      currentType
      currentAssetId
      currentPlaylistId
      currentScheduleId
      currentSelectionDate
      scale
      orientation
      tags
      muted
      activationDate
      creationDate
      location {
        coordinates
        name
      }
      feature
      temporarily
      scheduleGoLive
      recentAssignments
      assignItem {
        key
        value
        type
      }
      isContentExpired
      parentGroupInfo {
        _id
        name
        path
        teamId
        isFolderSecurityEdit
        isFolderSecurityView
        securities {
          userId
          role
        }
        securityTeams
      }
    }
  }
`;

export const SEARCH_ASSETS = gql`
  query SearchAssets($teamId: String, $input: SearchInput!) {
    searchAssets(teamId: $teamId, input: $input) {
      _id
      name
      type
      fileType
      appType
      webType
      youtubeType
      commonType
      subType
      serviceType
      originalFileName
      thumbnail
      bucket
      AWSS3ID
      processId
      path
      groupId
      teamId
      tags
      status
      engage
      contentStatus
      duration
      # The playback window (#2185) — same reason as ASSETS_QUERY in operations/assets.ts: the
      # picker switches to this query the moment the operator searches, and a trimmed asset added
      # from a search result must carry its window or it persists the full source length.
      startSeconds
      endSeconds
      emergencyDuration
      startDate
      currentDate
      isHide
      assetExpired {
        startDate
        endDate
        timezone
      }
      num_pages
      durationPage
      pageDurations
      createdAt
      lastUpdatedDate
      playlistId
      parentGroupInfo {
        _id
        name
        path
        teamId
        isFolderSecurityEdit
        isFolderSecurityView
        securities {
          userId
          role
        }
        securityTeams
      }
    }
  }
`;

export const SEARCH_PLAYLISTS = gql`
  query SearchPlaylists($teamId: String, $input: SearchInput!) {
    searchPlaylists(teamId: $teamId, input: $input) {
      _id
      name
      teamId
      path
      tags
      color
      createdAt
      lastUpdatedDate
      parentGroupInfo {
        _id
        name
        path
        teamId
        isFolderSecurityEdit
        isFolderSecurityView
        securities {
          userId
          role
        }
        securityTeams
      }
    }
  }
`;

export const SEARCH_GROUPS = gql`
  query SearchGroups($teamId: String, $input: SearchInput!) {
    searchGroups(teamId: $teamId, input: $input) {
      _id
      name
      type
      path
      description
      teamId
      thumbnail
      bucket
      createdAt
      lastUpdatedDate
      isFolderSecurityEdit
      isFolderSecurityView
      securities {
        userId
        role
      }
      securityTeams
      securitiesDetail {
        name
        userId
        role
      }
      securityTeamsDetail {
        name
        _id
      }
      sharedLink
    }
  }
`;

// --- Global search: split into two queries for progressive rendering ---
//
// Same backend resolver, different `include` allow-lists — the server returns
// empty arrays for sections not in the include list (no Atlas call, no DB
// query for those). GraphQL selection then omits those fields on the wire.
//
// GLOBAL_SEARCH (fast, ~150-250ms): SCREENS/PLAYLISTS/SCHEDULES/FOLDERS/
//   FEEDS/TEAMS/MEMBERS — small collections + regex or fast Atlas indexes.
//
// GLOBAL_SEARCH_ASSETS_ENGAGES (slow, ~700-1500ms on prod-scale ASSETs):
//   ASSETS/ENGAGES — one Atlas $search on the 8M+ asset collection, split
//   via $facet into the two partitions.
//
// GlobalSearchBar fires both in parallel. First paint lands under the
// "responsive" 250ms threshold; the slow bucket fills in behind a
// section-level spinner.

export const GLOBAL_SEARCH = gql`
  query GlobalSearch(
    $teamId: String
    $query: String!
    $limit: Int
    $offset: Int
    $modifiedAfter: String
    $sharedWithMe: Boolean
    $favoritesOnly: Boolean
    $titleOnly: Boolean
    $sort: GlobalSearchSort
    $dir: GlobalSearchSortDirection
    $filterTeamIds: [String!]
  ) {
    globalSearch(
      teamId: $teamId
      query: $query
      limit: $limit
      offset: $offset
      modifiedAfter: $modifiedAfter
      sharedWithMe: $sharedWithMe
      favoritesOnly: $favoritesOnly
      titleOnly: $titleOnly
      sort: $sort
      dir: $dir
      filterTeamIds: $filterTeamIds
      include: [
        SCREENS
        PLAYLISTS
        SCHEDULES
        OPERATIONAL_SCHEDULES
        FOLDERS
        FEEDS
        TEAMS
        MEMBERS
      ]
    ) {
      favoriteIds
      screensTotal
      playlistsTotal
      schedulesTotal
      operationalSchedulesTotal
      foldersTotal
      feedsTotal
      teamsTotal
      membersTotal
      screens {
        _id
        UUID
        deviceName
        name
        status
        lastHeartBeat
        os
        path
        teamId
        tags
        currentType
        currentAssetId
        currentPlaylistId
        currentScheduleId
        assignItem {
          key
          value
          type
        }
        recentAssignments
        temporarily
        feature
        scheduleGoLive
        flashMessage
        original
        statusContent
        isContentExpired
        lastUpdatedDate
        parentGroupInfo {
          _id
          name
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
        }
      }
      playlists {
        _id
        name
        color
        totalDuration
        tags
        lastUpdatedDate
        parentGroupInfo {
          _id
          name
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
        }
      }
      schedules {
        _id
        name
        type
        tags
        lastUpdatedDate
        parentGroupInfo {
          _id
          name
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
        }
      }
      operationalSchedules {
        _id
        name
        type
        tags
        lastUpdatedDate
        parentGroupInfo {
          _id
          name
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
        }
      }
      folders {
        _id
        name
        type
        path
        teamId
        isHoldDevice
        securities {
          userId
          role
        }
        securityTeams
        lastUpdatedDate
      }
      feeds {
        _id
        name
        tags
        thumbnail
        categoryIds
        status
        featured
      }
      teams {
        _id
        name
        description
        type
        memberCount
        lastUpdatedDate
      }
      members {
        userId
        name
        email
        role
        teamId
        teamName
        lastUpdatedDate
      }
    }
  }
`;

export const GLOBAL_SEARCH_ASSETS_ENGAGES = gql`
  query GlobalSearchAssetsEngages(
    $teamId: String
    $query: String!
    $limit: Int
    $offset: Int
    $modifiedAfter: String
    $sharedWithMe: Boolean
    $favoritesOnly: Boolean
    $titleOnly: Boolean
    $sort: GlobalSearchSort
    $dir: GlobalSearchSortDirection
    $filterTeamIds: [String!]
  ) {
    globalSearch(
      teamId: $teamId
      query: $query
      limit: $limit
      offset: $offset
      modifiedAfter: $modifiedAfter
      sharedWithMe: $sharedWithMe
      favoritesOnly: $favoritesOnly
      titleOnly: $titleOnly
      sort: $sort
      dir: $dir
      filterTeamIds: $filterTeamIds
      include: [ASSETS, ENGAGES]
    ) {
      favoriteIds
      assetsTotal
      engagesTotal
      assets {
        _id
        name
        originalFileName
        type
        fileType
        appType
        webType
        youtubeType
        commonType
        subType
        serviceType
        engage
        thumbnail
        bucket
        AWSS3ID
        tags
        path
        teamId
        status
        contentStatus
        isHide
        duration
        emergencyDuration
        assetExpired {
          startDate
          endDate
          timezone
        }
        lastUpdatedDate
        parentGroupInfo {
          _id
          name
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
        }
      }
      engages {
        _id
        name
        originalFileName
        type
        fileType
        appType
        webType
        youtubeType
        commonType
        subType
        serviceType
        engage
        thumbnail
        bucket
        AWSS3ID
        tags
        path
        teamId
        status
        contentStatus
        isHide
        duration
        emergencyDuration
        assetExpired {
          startDate
          endDate
          timezone
        }
        lastUpdatedDate
        parentGroupInfo {
          _id
          name
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
        }
      }
    }
  }
`;
